import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, MapPin, Briefcase, Heart, X, Lock, Crown, ArrowLeft, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { premiumAPI, profileAPI, interestAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import VerifiedBadge from '../components/VerifiedBadge';
import BackButton from '../components/ui/BackButton';
import SwipeableProfileCard from '../components/SwipeableProfileCard';
import DynamicProfileCard from '../components/DynamicProfileCard';
import { EDUCATION_OPTIONS, ISLAMIC_EDUCATION_OPTIONS, CANADIAN_PROVINCES, MADHHAB_OPTIONS, OCCUPATION_OPTIONS, PROVINCE_FULL_NAMES } from '../constants/profileOptions';
import { COUNTRIES, getProvincesForCountry } from '../constants/countries';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SearchPage = () => {
  const [showFilters, setShowFilters] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sentInterests, setSentInterests] = useState([]);
  const [receivedInterests, setReceivedInterests] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProfiles, setTotalProfiles] = useState(0);
  
  const [filters, setFilters] = useState({
    ageFrom: '',
    ageTo: '',
    religion: '',
    motherTongue: '',
    country: '',
    province: '',
    location: '',  // Keep for backward compatibility
    education: '',
    islamicEducation: '',
    occupation: '',
    madhhab: '',
    verifiedStatus: ''  // New: '', 'verified', 'unverified'
  });
  
  // Quick toggle for verified only (separate from smart filter)
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(false);
  
  // For cascading country/province filter
  const [availableProvinces, setAvailableProvinces] = useState([]);
  
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Helper function to get location display (Province, Country)
  const getLocationDisplay = (profile) => {
    if (!profile) return 'Location not specified';
    
    // Priority 1: Use country and province fields (new format)
    if (profile.country && profile.province) {
      const countryName = COUNTRIES.find(c => c.code === profile.country)?.name || profile.country;
      return `${profile.province}, ${countryName}`;
    }
    
    // Priority 2: Extract province from location field (legacy format)
    // Location format is "City, Province" - we want to show "Province, Country"
    if (profile.location) {
      const parts = profile.location.split(',');
      if (parts.length >= 2) {
        // Second part is province, hide city for privacy
        const province = parts[1].trim();
        
        // Try to get country from address object
        if (profile.address && profile.address.country) {
          const countryName = COUNTRIES.find(c => c.code === profile.address.country)?.name || profile.address.country;
          return `${province}, ${countryName}`;
        }
        
        // If no country in address, just show province
        return province;
      }
      
      // If location doesn't have comma, show as-is
      return profile.location;
    }
    
    return 'Location not specified';
  };


  // Load data when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to search profiles",
        variant: "destructive",
      });
      navigate('/login', { state: { returnUrl: '/search' } });
      return;
    }

    // If user reached here, they are verified (VerificationGuard handles it)
    loadPremiumStatus();
    fetchUsers(currentPage);
    loadSentInterests();
    loadReceivedInterests();
    loadShortlist();
  }, [isAuthenticated, navigate, toast]);

  // Fetch users when page changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers(currentPage);
    }
  }, [currentPage]);

  useEffect(() => {
    if (isAuthenticated) {
      loadPremiumStatus();
    }
  }, [isAuthenticated]);

  // Apply filters whenever profiles, filters or showVerifiedOnly change
  useEffect(() => {
    if (profiles.length > 0) {
      applyFilters();
    }
  }, [profiles, showVerifiedOnly]);


  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/user/search?page=${page}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        withCredentials: true
      });
      setProfiles(response.data.users || []);
      setFilteredProfiles(response.data.users || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalProfiles(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast({
        title: "Error",
        description: "Failed to load profiles",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPremiumStatus = async () => {
    try {
      const data = await premiumAPI.getStatus();
      setPremiumStatus(data);
    } catch (error) {
      console.error('Failed to load premium status:', error);
    }
  };

  const loadSentInterests = async () => {
    try {
      const data = await interestAPI.getSent();
      data.interests?.forEach(int => {
      });
      setSentInterests(data.interests || []);
      return data.interests || [];
    } catch (error) {
      console.error('❌ Failed to load sent interests:', error);
      return [];
    }
  };

  const loadReceivedInterests = async () => {
    try {
      const data = await interestAPI.getReceived();
      setReceivedInterests(data.interests || []);
      return data.interests || [];
    } catch (error) {
      console.error('❌ Failed to load received interests:', error);
      return [];
    }
  };

  
  const loadShortlist = async () => {
    try {
      const data = await profileAPI.getShortlist();
      const shortlistIds = data.shortlist?.map(p => p.id) || [];
      setShortlist(shortlistIds);
    } catch (error) {
      console.error('Failed to load shortlist:', error);
    }
  };

  const handleViewProfile = async (profileId) => {
    // Must be logged in and have premium to view profiles
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to view profiles",
        variant: "destructive",
      });
      navigate('/login', { state: { returnUrl: '/search' } });
      return;
    }
    
    if (!premiumStatus?.isPremium) {
      toast({
        title: "Premium Membership Required",
        description: "Upgrade to Premium to view full profiles",
        variant: "destructive",
      });
      navigate('/pricing');
      return;
    }
    
    // Premium users have unlimited profile views - no contact limit checking needed
    // Track contact view for statistics (don't block navigation)
    try {
      await premiumAPI.trackContactView();
    } catch (error) {
      // Don't block navigation even if tracking fails
    }
    
    // Navigate to profile with return URL so user can come back to search
    navigate(`/profile/${profileId}`, { state: { returnUrl: '/search' } });
  };

  const handleSendInterest = async (profileId) => {
    
    if (!premiumStatus?.isPremium) {
      toast({
        title: "Premium Required",
        description: "Upgrade to Premium to send interest",
        variant: "destructive",
      });
      navigate('/pricing');
      return;
    }
    
    try {
      const result = await profileAPI.sendInterest(profileId);
      
      toast({
        title: "Interest Sent!",
        description: "Your interest has been sent successfully",
      });
      
      // Wait a moment for backend to process
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload sent interests to update button status
      const updatedInterests = await loadSentInterests();
      
      const foundInterest = updatedInterests.find(int => {
        const receiverId = int.receiver?.id || int.receiverId;
        return receiverId === profileId;
      });
      
      if (foundInterest) {
      } else {
      }
    } catch (error) {
      console.error('❌ Error sending interest:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send interest",
        variant: "destructive",
      });
    }
  };

  const handleShortlist = async (profileId) => {
    try {
      if (shortlist.includes(profileId)) {
        // Remove from shortlist
        await profileAPI.removeFromShortlist(profileId);
        setShortlist(shortlist.filter(id => id !== profileId));
        toast({
          title: "Removed from Shortlist",
          description: "Profile has been removed from your shortlist",
        });
      } else {
        // Add to shortlist
        await profileAPI.addToShortlist(profileId);
        setShortlist([...shortlist, profileId]);
        toast({
          title: "Added to Shortlist",
          description: "Profile has been added to your shortlist",
        });
      }
    } catch (error) {
      console.error('Error updating shortlist:', error);
      toast({
        title: "Error",
        description: "Failed to update shortlist. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle swipe to send interest
  const handleSwipeInterest = async (profileId) => {
    try {
      await profileAPI.sendInterest(profileId);
      
      // Refetch sent interests to update UI
      const updatedSent = await interestAPI.getSent();
      setSentInterests(updatedSent.interests || []);
      
      toast({
        title: "Interest Sent! 💖",
        description: "Your interest has been sent successfully",
      });
    } catch (error) {
      console.error('Error sending interest:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send interest. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle accepting a received interest
  const handleAcceptInterest = async (profileId) => {
    try {
      // Find the received interest for this profile
      const receivedInterest = receivedInterests.find(int => {
        const senderId = int.sender?.id || int.fromUserId || int.from_user_id || int.senderId;
        return senderId === profileId;
      });

      if (!receivedInterest) {
        toast({
          title: "Error",
          description: "Interest not found",
          variant: "destructive",
        });
        return;
      }

      // Accept the interest
      await interestAPI.respond(receivedInterest.id, 'accepted');
      
      // Refetch received interests to update UI
      const updatedReceived = await interestAPI.getReceived();
      setReceivedInterests(updatedReceived.interests || []);
      
      toast({
        title: "Interest Accepted! 💚",
        description: "You can now message this person",
      });
    } catch (error) {
      console.error('Error accepting interest:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to accept interest. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle interest click - either send new interest or accept received interest
  const handleInterestAction = async (profileId) => {
    const interestStatus = getInterestStatus(profileId);
    
    if (interestStatus === 'received') {
      // Accept the received interest
      await handleAcceptInterest(profileId);
    } else if (!interestStatus || interestStatus === null) {
      // Send a new interest
      await handleSwipeInterest(profileId);
    }
    // If status is 'sent' or 'mutual', the button should be disabled so this shouldn't be called
  };

  // Get interest status for a profile
  const getInterestStatus = (profileId) => {
    
    // Check sent interests
    const sentInterest = sentInterests.find(int => {
      // Backend returns receiver object with id field
      const receiverId = int.receiver?.id || int.toUserId || int.to_user_id || int.receiverId;
      const matches = receiverId === profileId;
      return matches;
    });
    
    // Check received interests
    const receivedInterest = receivedInterests.find(int => {
      const senderId = int.sender?.id || int.fromUserId || int.from_user_id || int.senderId;
      return senderId === profileId;
    });
    
    // If either sent or received interest is accepted, it's a mutual match
    if (sentInterest?.status === 'accepted' || receivedInterest?.status === 'accepted') {
      return 'mutual';
    }
    
    // If user sent an interest that's pending
    if (sentInterest?.status === 'pending') {
      return 'sent';
    }
    
    // If user received an interest that's pending
    if (receivedInterest?.status === 'pending') {
      return 'received';
    }
    
    // No interest exists
    return null;
  };

  const applyFilters = () => {
    let filtered = profiles;
    
    // Quick toggle filter - show verified only
    if (showVerifiedOnly) {
      filtered = filtered.filter(p => p.isVerified || p.govIdVerified);
    }
    
    // Smart filter - verified status dropdown
    if (filters.verifiedStatus === 'verified') {
      filtered = filtered.filter(p => p.isVerified || p.govIdVerified);
    } else if (filters.verifiedStatus === 'unverified') {
      filtered = filtered.filter(p => !p.isVerified && !p.govIdVerified);
    }
    
    if (filters.ageFrom) {
      filtered = filtered.filter(p => p.age >= parseInt(filters.ageFrom));
    }
    if (filters.ageTo) {
      filtered = filtered.filter(p => p.age <= parseInt(filters.ageTo));
    }
    if (filters.religion) {
      filtered = filtered.filter(p => p.religion === filters.religion);
    }
    if (filters.motherTongue) {
      filtered = filtered.filter(p => p.motherTongue === filters.motherTongue);
    }
    if (filters.country) {
      filtered = filtered.filter(p => {
        // Check new format: country field
        if (p.country === filters.country) return true;
        
        // Check legacy format: address.country
        if (p.address && p.address.country === filters.country) return true;
        
        return false;
      });
    }
    if (filters.province) {
      filtered = filtered.filter(p => {
        // Check new format: province field
        if (p.province === filters.province) return true;
        
        // Check legacy format: extract from location "City, Province"
        if (p.location && p.location.includes(',')) {
          const parts = p.location.split(',');
          const locationProvince = parts[1]?.trim();
          if (locationProvince === filters.province) return true;
        }
        
        // Check legacy format: address.province
        if (p.address && p.address.province === filters.province) return true;
        
        return false;
      });
    }
    if (filters.education) {
      filtered = filtered.filter(p => p.education === filters.education);
    }
    if (filters.islamicEducation) {
      filtered = filtered.filter(p => p.islamicEducation === filters.islamicEducation);
    }
    if (filters.occupation) {
      filtered = filtered.filter(p => p.occupation === filters.occupation);
    }
    if (filters.madhhab) {
      filtered = filtered.filter(p => p.religiousInfo?.madhhab === filters.madhhab);
    }
    
    setFilteredProfiles(filtered);
  };

  const resetFilters = () => {
    setFilters({
      ageFrom: '',
      ageTo: '',
      religion: '',
      motherTongue: '',
      country: '',
      province: '',
      location: '',
      education: '',
      islamicEducation: '',
      occupation: '',
      madhhab: '',
      verifiedStatus: ''
    });
    setShowVerifiedOnly(false);
    setAvailableProvinces([]);
    setCurrentPage(1);  // Reset to first page
    setFilteredProfiles(profiles);
  };

  // Show loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-2 py-8">
        <BackButton to="/" />
        
        {/* Trial Countdown Banner - Only show on Dashboard/Search for trial users */}
        {premiumStatus?.isFreeTrial && premiumStatus?.daysRemaining !== null && (
          <div className={`mb-6 rounded-xl p-4 flex items-center justify-between ${
            premiumStatus.daysRemaining <= 3 
              ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' 
              : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
          }`}>
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6" />
              <div>
                <p className="font-semibold">
                  {premiumStatus.daysRemaining <= 3 
                    ? `Only ${premiumStatus.daysRemaining} day${premiumStatus.daysRemaining !== 1 ? 's' : ''} left!` 
                    : `Your free trial ends in ${premiumStatus.daysRemaining} days`}
                </p>
                <p className="text-sm opacity-90">
                  {premiumStatus.daysRemaining <= 3 
                    ? 'Upgrade now to keep your premium features' 
                    : 'Enjoying all premium features'}
                </p>
              </div>
            </div>
            <Link to="/pricing">
              <Button className="bg-white text-gray-900 hover:bg-gray-100 font-semibold">
                Upgrade Now
              </Button>
            </Link>
          </div>
        )}

        {/* Trial Expired Banner - Show when trial ended */}
        {premiumStatus?.freeTrialUsed && !premiumStatus?.isPremium && (
          <div className="mb-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6" />
              <div>
                <p className="font-semibold">Your free trial has ended</p>
                <p className="text-sm opacity-90">Upgrade to continue enjoying premium features</p>
              </div>
            </div>
            <Link to="/pricing">
              <Button className="bg-white text-gray-900 hover:bg-gray-100 font-semibold">
                Upgrade Now
              </Button>
            </Link>
          </div>
        )}
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Search Profiles</h1>
          <p className="text-gray-600">Find your perfect match from thousands of verified profiles</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar */}
          {showFilters && (
            <div className="lg:w-80 flex-shrink-0">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold flex items-center">
                      <SlidersHorizontal className="w-5 h-5 mr-2" />
                      Filters
                    </h2>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="lg:hidden text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Verified Status Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-mint-500" />
                        Verified Status
                      </label>
                      <Select value={filters.verifiedStatus} onValueChange={(value) => setFilters({...filters, verifiedStatus: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="All Profiles" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Profiles</SelectItem>
                          <SelectItem value="verified">✓ Verified Only</SelectItem>
                          <SelectItem value="unverified">Unverified Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Age Range
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder="From"
                          value={filters.ageFrom}
                          onChange={(e) => setFilters({...filters, ageFrom: e.target.value})}
                        />
                        <Input
                          type="number"
                          placeholder="To"
                          value={filters.ageTo}
                          onChange={(e) => setFilters({...filters, ageTo: e.target.value})}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Madhhab (School of Thought)
                      </label>
                      <Select value={filters.madhhab} onValueChange={(value) => setFilters({...filters, madhhab: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Madhhab" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px] overflow-y-auto">
                          {MADHHAB_OPTIONS.map(option => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Language
                      </label>
                      <Select value={filters.motherTongue} onValueChange={(value) => setFilters({...filters, motherTongue: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Language" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="English">English</SelectItem>
                          <SelectItem value="Arabic">Arabic</SelectItem>
                          <SelectItem value="Urdu">Urdu</SelectItem>
                          <SelectItem value="Hindi">Hindi</SelectItem>
                          <SelectItem value="Gujarati">Gujarati</SelectItem>
                          <SelectItem value="French">French</SelectItem>
                          <SelectItem value="Spanish">Spanish</SelectItem>
                          <SelectItem value="Punjabi">Punjabi</SelectItem>
                          <SelectItem value="Bengali">Bengali</SelectItem>
                          <SelectItem value="Somali">Somali</SelectItem>
                          <SelectItem value="Turkish">Turkish</SelectItem>
                          <SelectItem value="Persian">Persian (Farsi)</SelectItem>
                          <SelectItem value="Pashto">Pashto</SelectItem>
                          <SelectItem value="Malay">Malay</SelectItem>
                          <SelectItem value="Indonesian">Indonesian</SelectItem>
                          <SelectItem value="Swahili">Swahili</SelectItem>
                          <SelectItem value="Hausa">Hausa</SelectItem>
                          <SelectItem value="Kurdish">Kurdish</SelectItem>
                          <SelectItem value="Albanian">Albanian</SelectItem>
                          <SelectItem value="Bosnian">Bosnian</SelectItem>
                          <SelectItem value="Tagalog">Tagalog</SelectItem>
                          <SelectItem value="Amharic">Amharic</SelectItem>
                          <SelectItem value="Yoruba">Yoruba</SelectItem>
                          <SelectItem value="Dari">Dari</SelectItem>
                          <SelectItem value="Sindhi">Sindhi</SelectItem>
                          <SelectItem value="Tamil">Tamil</SelectItem>
                          <SelectItem value="Telugu">Telugu</SelectItem>
                          <SelectItem value="Kannada">Kannada</SelectItem>
                          <SelectItem value="Malayalam">Malayalam</SelectItem>
                          <SelectItem value="Marathi">Marathi</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country
                      </label>
                      <Select value={filters.country} onValueChange={(value) => {
                        const provinces = getProvincesForCountry(value);
                        setAvailableProvinces(provinces);
                        setFilters({...filters, country: value, province: ''});
                      }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Province/State
                      </label>
                      <Select 
                        value={filters.province} 
                        onValueChange={(value) => setFilters({...filters, province: value})}
                        disabled={!filters.country}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={filters.country ? "Select Province/State" : "Select country first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableProvinces.map((province) => (
                            <SelectItem key={province} value={province}>
                              {province}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Education
                      </label>
                      <Select value={filters.education} onValueChange={(value) => setFilters({...filters, education: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Education" />
                        </SelectTrigger>
                        <SelectContent>
                          {EDUCATION_OPTIONS.map((education) => (
                            <SelectItem key={education} value={education}>
                              {education}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Islamic Education
                      </label>
                      <Select value={filters.islamicEducation} onValueChange={(value) => setFilters({...filters, islamicEducation: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Islamic Education" />
                        </SelectTrigger>
                        <SelectContent>
                          {ISLAMIC_EDUCATION_OPTIONS.map((islamicEdu) => (
                            <SelectItem key={islamicEdu} value={islamicEdu}>
                              {islamicEdu}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Occupation
                      </label>
                      <Select value={filters.occupation} onValueChange={(value) => setFilters({...filters, occupation: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Occupation" />
                        </SelectTrigger>
                        <SelectContent>
                          {OCCUPATION_OPTIONS.map((occupation) => (
                            <SelectItem key={occupation} value={occupation}>
                              {occupation}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={applyFilters} className="flex-1 bg-mint-500 hover:bg-mint-600">
                        Apply Filters
                      </Button>
                      <Button onClick={resetFilters} variant="outline" className="flex-1">
                        Reset
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Results */}
          <div className="flex-1">
            {/* Mobile Filter Toggle */}
            {!showFilters && (
              <Button
                onClick={() => setShowFilters(true)}
                variant="outline"
                className="mb-4 lg:hidden"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Show Filters
              </Button>
            )}

            {/* Quick Verified Toggle */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
              <p className="text-gray-600">
                Showing <span className="font-semibold">{filteredProfiles.length}</span> profiles
                {showVerifiedOnly && <span className="text-mint-600 ml-1">(Verified Only)</span>}
              </p>
              
              {/* Verified Only Quick Toggle */}
              <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg shadow-sm border">
                <ShieldCheck className={`w-5 h-5 ${showVerifiedOnly ? 'text-mint-500' : 'text-gray-400'}`} />
                <span className="text-sm font-medium text-gray-700">Verified Only</span>
                <button
                  onClick={() => setShowVerifiedOnly(!showVerifiedOnly)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showVerifiedOnly ? 'bg-mint-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showVerifiedOnly ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Profile Grid - Admin Designed Cards Only */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProfiles.map((profile) => {
                const interestStatus = getInterestStatus(profile.id);
                return (
                  <DynamicProfileCard
                    key={profile.id}
                    profile={profile}
                    onCardClick={() => handleViewProfile(profile.id)}
                    onInterestClick={() => handleInterestAction(profile.id)}
                    isInterestSent={interestStatus === 'sent'}
                    isInterestReceived={interestStatus === 'received'}
                    isMutualMatch={interestStatus === 'mutual'}
                    isPremium={premiumStatus?.isPremium}
                    isShortlisted={shortlist.includes(profile.id)}
                    onShortlistToggle={(profileId) => handleShortlist(profileId)}
                  />
                );
              })}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg shadow-md px-6 py-4">
                <div className="text-sm text-gray-600">
                  Showing page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
                  <span className="ml-2 text-gray-400">({totalProfiles} total profiles)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                    className="hidden sm:flex"
                  >
                    First
                  </Button>
                  <Button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    className="flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  
                  {/* Page numbers */}
                  <div className="hidden sm:flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          className={currentPage === pageNum ? "bg-mint-500 hover:bg-mint-600" : ""}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    className="flex items-center gap-1"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="sm"
                    className="hidden sm:flex"
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}

            {/* No results message */}
            {filteredProfiles.length === 0 && !loading && (
              <div className="text-center py-12 bg-white rounded-lg shadow-md">
                <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No profiles found</h3>
                <p className="text-gray-500">Try adjusting your filters to see more profiles</p>
              </div>
            )}

            {/* OLD CARDS - KEEPING AS BACKUP, WILL REMOVE AFTER TESTING */}
            <div className="hidden grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredProfiles.map((profile) => (
                <Card key={profile.id} className="hover:shadow-lg transition-shadow relative">
                  <CardContent className="p-0">
                    <div className="relative">
                      <img
                        src={profile.image}
                        alt={`${profile.firstName} ${profile.lastName}`}
                        className={`w-full h-64 object-cover rounded-t-lg ${
                          !premiumStatus?.isPremium ? 'blur-md' : ''
                        }`}
                      />
                      {!premiumStatus?.isPremium && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-t-lg">
                          <div className="text-center text-white">
                            <Lock className="w-12 h-12 mx-auto mb-2" />
                            <p className="font-semibold text-lg">Premium Required</p>
                            <p className="text-sm">View clear photos</p>
                          </div>
                        </div>
                      )}
                      {premiumStatus?.isPremium && (
                        <button
                          onClick={() => handleShortlist(profile.id)}
                          className={`absolute top-3 left-3 p-2 rounded-full ${
                            shortlist.includes(profile.id)
                              ? 'bg-coral-500 text-white'
                              : 'bg-white text-gray-700'
                          } hover:scale-110 transition-transform shadow-md`}
                        >
                          <Heart className={`w-5 h-5 ${shortlist.includes(profile.id) ? 'fill-current' : ''}`} />
                        </button>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-semibold">
                          {profile.firstName} {profile.lastName}
                        </h3>
                        {(profile.isVerified || profile.govIdVerified) && <VerifiedBadge size="default" showText={false} />}
                      </div>
                      
                      {/* Show limited info for non-premium, full info for premium */}
                      {premiumStatus?.isPremium ? (
                        <div className="space-y-2 text-sm text-gray-600 mb-4">
                          <p>{profile.age} years, {profile.height || 'Not specified'}</p>
                          <p className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {getLocationDisplay(profile)}
                          </p>
                          <p className="flex items-center">
                            <Briefcase className="w-4 h-4 mr-1" />
                            {profile.occupation}
                          </p>
                          <p>{profile.religiousInfo?.madhhab || 'Madhhab not specified'}, {profile.motherTongue}</p>
                          <p className="text-green-600 text-xs">{profile.lastActive}</p>
                        </div>
                      ) : (
                        <div className="space-y-2 text-sm text-gray-600 mb-4">
                          <p>{profile.age} years</p>
                          <p className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {getLocationDisplay(profile)}
                          </p>
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                            <div className="flex items-center gap-2 text-yellow-800">
                              <Crown className="w-4 h-4" />
                              <p className="text-xs font-semibold">Upgrade to Premium to view full profile details</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleViewProfile(profile.id)}
                          className="flex-1 bg-mint-500 hover:bg-mint-600"
                        >
                          {premiumStatus?.isPremium ? 'View Profile' : (
                            <span className="flex items-center gap-1">
                              <Lock className="w-4 h-4" />
                              View Profile
                            </span>
                          )}
                        </Button>
                        {(() => {
                          const interestStatus = getInterestStatus(profile.id);
                          
                          if (interestStatus === 'accepted') {
                            // If interest accepted, show "Send Message" button
                            return (
                              <Button 
                                onClick={() => navigate(`/messages/${profile.id}`, { 
                                  state: { returnUrl: '/search', from: 'search' } 
                                })}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                              >
                                Send Message
                              </Button>
                            );
                          } else if (interestStatus === 'pending') {
                            // If interest pending, show "Pending" button (disabled)
                            return (
                              <Button 
                                disabled
                                variant="outline" 
                                className="flex-1 opacity-60 cursor-not-allowed"
                              >
                                Pending
                              </Button>
                            );
                          } else {
                            // If no interest sent, show "Send Interest" button
                            return (
                              <Button 
                                onClick={() => handleSendInterest(profile.id)}
                                variant="outline" 
                                className="flex-1"
                              >
                                {premiumStatus?.isPremium ? 'Send Interest' : (
                                  <span className="flex items-center gap-1">
                                    <Lock className="w-4 h-4" />
                                    Interest
                                  </span>
                                )}
                              </Button>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;